import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";

/**
 * App-wide handle to the "MAKEJ!" agent widget (Block 3 B3.1). Pages call `openChat(...)`
 * to pop the floating panel with a seeded prompt (e.g. a lead's "Build offer" button); the
 * widget itself consumes `pendingSeed` once and clears it. `enabled` comes from
 * GET /agent/status so launchers/buttons stay hidden until the Anthropic key is configured.
 */
interface AgentContextValue {
  enabled: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Open the panel and queue a message to auto-send (consumed once by the widget). */
  openChat: (message?: string) => void;
  pendingSeed: string | null;
  consumeSeed: () => void;
}

const AgentCtx = createContext<AgentContextValue | null>(null);

export function AgentProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSeed, setPendingSeed] = useState<string | null>(null);

  // One cheap status check per session; the launcher and contextual buttons gate on it.
  const { data } = useQuery({
    queryKey: ["agent", "status"],
    queryFn: () => api.get<ApiResponse<{ enabled: boolean }>>("/api/admin/agent/status"),
    staleTime: 5 * 60_000,
  });
  const enabled = !!data?.data?.enabled;

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const openChat = useCallback((message?: string) => {
    setIsOpen(true);
    if (message) setPendingSeed(message);
  }, []);
  const consumeSeed = useCallback(() => setPendingSeed(null), []);

  const value = useMemo<AgentContextValue>(
    () => ({ enabled, isOpen, open, close, openChat, pendingSeed, consumeSeed }),
    [enabled, isOpen, open, close, openChat, pendingSeed, consumeSeed],
  );

  return <AgentCtx.Provider value={value}>{children}</AgentCtx.Provider>;
}

export function useAgent(): AgentContextValue {
  const ctx = useContext(AgentCtx);
  if (!ctx) throw new Error("useAgent must be used within <AgentProvider>");
  return ctx;
}
